import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Pagination,
  PaginationItem,
  useMediaQuery,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import { capitalizeFirstLetter } from "../../components/reusableComponent/CapitalLetter";
import ReactModal from "react-modal";
import SLBreachFilterModal from "./SlBreachFilterModal";
import { useTicketWorkflowIdQuery } from "../../Services/dashboard/dashboard";
import { ACTIVITY_PRIMARY, activityToolbarOutlinedSx } from "./activityUiTokens";

const SL_TYPE_OPTIONS = [
  { value: "workflow", label: "Workflow SL" },
  { value: "priority", label: "Priority SL" },
  { value: "tag", label: "Tag SL" },
] as const;

type SlTypeValue = (typeof SL_TYPE_OPTIONS)[number]["value"];

const getSlTargetColumnLabel = (
  taskSource: string,
  slType: SlTypeValue,
): string => {
  if (taskSource !== "tickets") return "First response target";
  if (slType === "priority") return "Priority SL target";
  if (slType === "tag") return "Tag SL target";
  return "Workflow SL target";
};

const emptyTicketFilters = {
  id: "",
  subject: "",
  customer_name: "",
  assignee: "",
  priority: "",
  tag_id: "",
  workflow_stage: "",
  status_type: "",
  status_id: "",
};

const emptyEmailFilters = {
  subject: "",
  customer_name: "",
  assignee: "",
};

const customFilterModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: "50px",
    margin: "auto",
    Overflow: "hidden",
    OverflowY: "auto",
  },
};

const pageSizeOptions = [10, 20, 50];

const SCROLL_POSITION_KEY = "slBreachedTasksScrollPosition";
const LAST_VIEWED_TASK_KEY = "lastViewedSLBreached";

const usePaginationState = (taskSource: string) => {
  const storageKey = `slbreach_${taskSource || "all"}_pagination`;

  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = sessionStorage.getItem(`${storageKey}_pageSize`);
    return saved ? parseInt(saved, 10) : 10;
  });

  const [currentPage, setCurrentPage] = useState<number>(() => {
    const saved = sessionStorage.getItem(`${storageKey}_currentPage`);
    return saved ? parseInt(saved, 10) : 1;
  });

  const updatePageSize = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    sessionStorage.setItem(`${storageKey}_pageSize`, newSize.toString());
    sessionStorage.setItem(`${storageKey}_currentPage`, "1");
  };

  const updateCurrentPage = (newPage: number) => {
    setCurrentPage(newPage);
    sessionStorage.setItem(`${storageKey}_currentPage`, newPage.toString());
  };

  const refreshState = () => {
    const savedPageSize = sessionStorage.getItem(`${storageKey}_pageSize`);
    const savedCurrentPage = sessionStorage.getItem(`${storageKey}_currentPage`);
    setPageSize(savedPageSize ? parseInt(savedPageSize, 10) : 10);
    setCurrentPage(savedCurrentPage ? parseInt(savedCurrentPage, 10) : 1);
  };

  return {
    pageSize,
    currentPage,
    updatePageSize,
    updateCurrentPage,
    refreshState,
  };
};

type SLBreachedTaskProps = {
  task_source: "emails" | "tickets";
  useQueryHook: any;
};

// Function to format time string (HH:MM:SS) to human-readable format with elapsed time
const formatBreachTime = (
  timeString: string,
  componentMountedTime: Date
): string => {
  if (!timeString) return "0s";

  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  const initialTotalSeconds = hours * 3600 + minutes * 60 + seconds;

  // Calculate elapsed time since component mounted
  const now = new Date();
  const elapsedSeconds = Math.floor(
    (now.getTime() - componentMountedTime.getTime()) / 1000
  );
  const updatedSeconds = initialTotalSeconds + elapsedSeconds;

  const hrs = Math.floor(updatedSeconds / 3600);
  const mins = Math.floor((updatedSeconds % 3600) / 60);
  const secs = updatedSeconds % 60;

  if (hrs > 0) {
    return `${hrs}h ${mins.toString().padStart(2, "0")}m ${secs
      .toString()
      .padStart(2, "0")}s`;
  } else if (mins > 0) {
    return `${mins}m ${secs.toString().padStart(2, "0")}s`;
  } else {
    return `${secs}s`;
  }
};

const SLBreachedTask: React.FC<SLBreachedTaskProps> = ({
  task_source,
  useQueryHook,
}) => {
  const [componentMountedTime] = useState<Date>(new Date());
  const [isFilterModalOpen, setFilterIsModalOpen] = useState(false);
  const [isFilterOn, setIsFilterOn] = useState<boolean>(false);
  const isSmallScreen = useMediaQuery("(max-width:575px)");
  const isPageScreen = useMediaQuery("(max-width:510px)");
  const isExtraSmallScreen = useMediaQuery("(max-width:442px)");

  // Initialize workflow from sessionStorage
  const [selectedWorkFlow, setSelectedWorkFlow] = useState<string>(() => {
    const savedWorkflow = sessionStorage.getItem(
      `slBreachWorkflow_${task_source}`
    );
    return savedWorkflow || "";
  });

  const [selectedSlType, setSelectedSlType] = useState<SlTypeValue>(() => {
    const saved = sessionStorage.getItem(`slBreachSlType_${task_source}`);
    return (saved as SlTypeValue) || "workflow";
  });

  const [currentTime, setCurrentTime] = useState<number>(Date.now());

  // Refs for scroll position management (same pattern as AccountCards)
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRestoreCleanupRef = useRef<(() => void) | null>(null);

  const [selectedFilters1, setSelectedFilters1] = useState<any>(() => {
    const sessionData = sessionStorage.getItem(
      `slBreachFilterValues_${task_source}`
    );
    return sessionData
      ? JSON.parse(sessionData)
      : task_source === "tickets"
      ? { ...emptyTicketFilters }
      : { ...emptyEmailFilters };
  });

  const {
    pageSize,
    currentPage,
    updatePageSize,
    updateCurrentPage,
    refreshState,
  } = usePaginationState(task_source);

  // Load saved workflow and filters when task_source changes
  useEffect(() => {
    refreshState();

    // Load saved workflow for this task source
    const savedWorkflow = sessionStorage.getItem(
      `slBreachWorkflow_${task_source}`
    );
    setSelectedWorkFlow(savedWorkflow || "");

    const savedSlType = sessionStorage.getItem(`slBreachSlType_${task_source}`);
    setSelectedSlType((savedSlType as SlTypeValue) || "workflow");

    // Load saved filters for this task source
    const savedFilters = sessionStorage.getItem(
      `slBreachFilterValues_${task_source}`
    );
    if (savedFilters) {
      setSelectedFilters1(JSON.parse(savedFilters));
    }
  }, [task_source]);

  // Save workflow to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(`slBreachWorkflow_${task_source}`, selectedWorkFlow);
  }, [selectedWorkFlow, task_source]);

  useEffect(() => {
    sessionStorage.setItem(`slBreachSlType_${task_source}`, selectedSlType);
  }, [selectedSlType, task_source]);

  useEffect(() => {
    if (
      selectedFilters1 &&
      Object.values(selectedFilters1).some((v) => v !== "")
    ) {
      setIsFilterOn(true);
    } else {
      setIsFilterOn(false);
    }
  }, [selectedFilters1]);

  useEffect(() => {
    sessionStorage.setItem(
      `slBreachFilterValues_${task_source}`,
      JSON.stringify(selectedFilters1)
    );
  }, [selectedFilters1, task_source]);

  // Update time every second for real-time counter
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);
  
  // Store scroll position before page unload (e.g. refresh)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const scrollableContainer = scrollContainerRef.current;
      if (scrollableContainer) {
        sessionStorage.setItem(
          SCROLL_POSITION_KEY,
          String(scrollableContainer.scrollTop)
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const selectedTeam = localStorage.getItem("teamId");

  // Workflow API - fetch for both emails and tickets
  const { data: workflow } = useTicketWorkflowIdQuery(selectedTeam, {
    skip: !selectedTeam,
  });

  const { data: SlBreachData, isLoading } = useQueryHook({
    currentPage,
    pageSize,
    selectedWorkFlow,
    task_source,
    selectedActiveFilters1: selectedFilters1,
    isFilterOn: isFilterOn || !!selectedFilters1,
    ...(task_source === "tickets" ? { sl_type: selectedSlType } : {}),
  });

  const normalizePriority = (priority: string) => {
    if (!priority) return "N/A";
    const normalized = priority.toLowerCase();
    if (normalized === "urgent") return "Urgent";
    if (normalized === "high") return "High";
    if (normalized === "medium") return "Medium";
    if (normalized === "low") return "Low";
    return capitalizeFirstLetter(priority);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    // Format time in 12-hour format with AM/PM
    let hours = date.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12 for 12 AM
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
  };

  const formatTimeSinceBreach = (timeString: string): string => {
    if (!timeString) return "N/A";

    const parts = timeString.split(":");
    if (parts.length === 3) {
      const totalHours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);

      if (totalHours > 24) {
        const days = Math.floor(totalHours / 24);
        const hours = totalHours % 24;
        return `${days}d ${hours}h`;
      }
      return `${totalHours}h ${minutes}m`;
    }

    return timeString;
  };

  const formatCustomerTag = (item: any): string => {
    const raw =
      item?.customer_tag ??
      item?.tag ??
      item?.customer_tags ??
      item?.tags;
    if (raw == null || raw === "") return "N/A";
    if (Array.isArray(raw)) {
      const names = raw
        .map((tag) =>
          typeof tag === "string" ? tag : tag?.tag ?? tag?.name ?? tag?.tag_name ?? ""
        )
        .filter(Boolean);
      return names.length ? names.join(", ") : "N/A";
    }
    return String(raw);
  };

  const formatWorkflowStage = (item: any): string => {
    const raw =
      item?.workflow_stage ??
      item?.stage_name ??
      item?.stage ??
      item?.current_stage ??
      item?.workflow_stage_name;
    if (raw == null || raw === "") return "N/A";
    return String(raw);
  };

  const rows =
    SlBreachData?.results?.map((item: any) => ({
      id: item?.id,
      subject: item?.subject_or_title || "N/A",
      customer_name: item?.customer_name || "N/A",
      customer_tag: formatCustomerTag(item),
      current_assignee: item?.assignee || "Unassigned",
      priority: normalizePriority(item?.priority),
      disposition_name: item?.disposition || "N/A",
      workflow_stage: formatWorkflowStage(item),
      sla_breach_time: item?.sla_breach_time
        ? formatDate(item?.sla_breach_time)
        : "N/A",
      time_since_breach: item?.time_since_breach,
      task_source: item?.source || task_source,
      escalation_level: item?.escalation_level || "N/A",
    })) || [];

  const ticketTableColSpan = 11;
  const emailTableColSpan = 8;

  // Restore scroll position when list is visible (not loading, has results)
  useEffect(() => {
    const canRestore =
      !isLoading &&
      rows.length > 0;
    if (canRestore) {
      const timer = setTimeout(() => {
        const cleanup = restoreScrollPosition();
        if (typeof cleanup === "function") {
          scrollRestoreCleanupRef.current = cleanup;
        }
      }, 50);
      return () => {
        clearTimeout(timer);
        if (scrollRestoreCleanupRef.current) {
          scrollRestoreCleanupRef.current();
          scrollRestoreCleanupRef.current = null;
        }
      };
    }
  }, [isLoading, rows]);

  const totalPages = Math.ceil((SlBreachData?.count || 0) / pageSize);

  const openFilterModal = () => setFilterIsModalOpen(true);
  const closeFilterModal = () => setFilterIsModalOpen(false);

  const handleSubmit = (filterValues) => {
    setSelectedFilters1(filterValues);
    updateCurrentPage(1);
  };

  // Store scroll position when navigating away (use element that actually scrolls)
  const storeScrollPosition = (taskId: string) => {
    const scrollableContainer = scrollContainerRef.current;
    if (scrollableContainer) {
      sessionStorage.setItem(
        SCROLL_POSITION_KEY,
        String(scrollableContainer.scrollTop)
      );
    }
    sessionStorage.setItem(LAST_VIEWED_TASK_KEY, taskId);
  };

  // Restore scroll position when returning (retry until content is laid out)
  const restoreScrollPosition = (): (() => void) => {
    const savedScrollPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
    if (!savedScrollPosition) return () => {};

    const targetScrollTop = parseInt(savedScrollPosition, 10);
    if (Number.isNaN(targetScrollTop) || targetScrollTop < 0) return () => {};

    const applyScroll = () => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTop = targetScrollTop;
    };

    const delays = [0, 50, 150, 300, 500, 800, 1200, 1800, 2500, 3500];
    const timeouts = delays.map((d) => setTimeout(applyScroll, d));
    requestAnimationFrame(() => {
      requestAnimationFrame(applyScroll);
      clearScrollPosition();
    });
    return () => {
      timeouts.forEach((t) => clearTimeout(t));
    };
  };

  const clearScrollPosition = () => {
    sessionStorage.removeItem(SCROLL_POSITION_KEY);
    sessionStorage.removeItem(LAST_VIEWED_TASK_KEY);
  };

  const handleWorkflowChange = (event) => {
    setSelectedWorkFlow(event.target.value);
    setSelectedFilters1((prev: any) => ({
      ...prev,
      workflow_stage: "",
      status_type: "",
      status_id: "",
    }));
    updateCurrentPage(1);
  };

  const handleSlTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as SlTypeValue;
    setSelectedSlType(value);
    setSelectedFilters1((prev: any) => ({
      ...prev,
      priority: "",
      tag_id: "",
      workflow_stage: "",
      status_type: "",
      status_id: "",
    }));
    updateCurrentPage(1);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    updateCurrentPage(page);
    clearScrollPosition();
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const newSize = Number(event.target.value);
    updatePageSize(newSize);
    clearScrollPosition();
  };

  const handleExportCSV = () => {
    if (!rows.length) {
      alert("No data to export");
      return;
    }

    const slTargetHeader = getSlTargetColumnLabel(task_source, selectedSlType);
    const headers =
      task_source === "tickets"
        ? [
            '"Sr.no."',
            '"Id"',
            '"Subject/Title"',
            '"Customer name"',
            '"Customer tag"',
            '"Assignee"',
            '"Priority"',
            '"Disposition"',
            '"Workflow status"',
            `"${slTargetHeader}"`,
            '"Time since breach"',
          ]
        : [
            '"Sr.no."',
            '"Subject/Title"',
            '"Customer name"',
            '"Assignee"',
            '"Disposition"',
            `"${slTargetHeader}"`,
            '"SL level"',
            '"Time since breach"',
          ];

    const csvContent = [
      headers.join(","),
      ...rows.map((row, index) => {
        const srNo = (currentPage - 1) * pageSize + index + 1;
        const rowData =
          task_source === "tickets"
            ? [
                srNo,
                row.id,
                `"${row.subject.replace(/"/g, '""')}"`,
                `"${row.customer_name.replace(/"/g, '""')}"`,
                `"${row.customer_tag.replace(/"/g, '""')}"`,
                `"${row.current_assignee.replace(/"/g, '""')}"`,
                row.priority,
                `"${row.disposition_name.replace(/"/g, '""')}"`,
                `"${row.workflow_stage.replace(/"/g, '""')}"`,
                `"${row.sla_breach_time || ""}"`,
                `"${row.time_since_breach || ""}"`,
              ]
            : [
                srNo,
                `"${row.subject.replace(/"/g, '""')}"`,
                `"${row.customer_name.replace(/"/g, '""')}"`,
                `"${row.current_assignee.replace(/"/g, '""')}"`,
                `"${row.disposition_name.replace(/"/g, '""')}"`,
                `"${row.sla_breach_time || ""}"`,
                `"${row.escalation_level || ""}"`,
                `"${row.time_since_breach || ""}"`,
              ];
        return rowData.join(",");
      }),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute(
      "download",
      `sl_breach_${task_source}_report_${
        new Date().toISOString().split("T")[0]
      }.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const stickyCellStyle = {
    position: "sticky" as const,
    top: 0,
    backgroundColor: "#f9fafb",
    zIndex: 10,
    fontWeight: 600,
    fontSize: isSmallScreen ? "13px" : "15px",
    padding: isSmallScreen ? "8px 4px" : "16px",
    minWidth: isSmallScreen ? "80px" : "auto",
    whiteSpace: "nowrap" as const,
  };
  const stickyCheckboxCellStyle = {
    position: "sticky" as const,
    top: 0,
    backgroundColor: "#f9fafb",
    zIndex: 11,
    fontWeight: 600,
    fontSize: isSmallScreen ? "13px" : "15px",
    whiteSpace: "nowrap" as const,
    padding: isSmallScreen ? "8px 4px" : "16px",
  };

  const actionButtonSx = {
    ...activityToolbarOutlinedSx,
    height: isSmallScreen ? 35 : 40,
    minWidth: isExtraSmallScreen ? "100%" : isSmallScreen ? 120 : 140,
    width: isExtraSmallScreen ? "100%" : isSmallScreen ? 120 : 140,
    gap: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: isSmallScreen ? "11px" : "14px",
  };

  return (
    <>
      <div
        className="card-toolbar flex-row-fluid justify-content-start d-flex align-items-center mb-5"
        style={{
          marginLeft: isSmallScreen ? "10px" : "30px",
          marginRight: isSmallScreen ? "-35px" : "",
        }}
      >
        <h2
          style={{
            margin: 0,
            marginLeft: "-28px",
            fontSize: isExtraSmallScreen ? "13px" : isSmallScreen ? "16px" : "",
            whiteSpace: "nowrap",
          }}
        >
          SL breached
        </h2>
      </div>

      <div
        className={`d-flex justify-content-between align-items-center mb-5 flex-wrap ${isExtraSmallScreen ? "gap-3" : ""}`}
        style={{
          marginLeft: isSmallScreen ? "10px" : "30px",
          marginRight: isSmallScreen ? "10px" : "30px",
          marginTop: "16px",
          gap: isSmallScreen ? "12px" : "16px",
        }}
      >
        <div
          className={`d-flex align-items-center gap-3 ${isExtraSmallScreen ? "flex-wrap w-100" : ""}`}
          style={{ marginLeft: "-28px" }}
        >
          {task_source === "tickets" && (
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div
                style={{
                  position: "absolute",
                  top: "-9px",
                  left: "5px",
                  background: "white",
                  padding: "0 5px",
                  zIndex: 1,
                }}
              >
                <p style={{ margin: "0", fontSize: "12px", color: "#666" }}>
                  SL type
                </p>
              </div>
              <select
                className="form-select"
                onChange={handleSlTypeChange}
                value={selectedSlType}
                style={{
                  height: 40,
                  width: isSmallScreen ? 130 : 140,
                }}
              >
                {SL_TYPE_OPTIONS.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    style={{
                      fontSize: isSmallScreen ? "11px" : "14px",
                    }}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div
              style={{
                position: "absolute",
                top: "-9px",
                left: "5px",
                background: "white",
                padding: "0 5px",
                zIndex: 1,
              }}
            >
              <p style={{ margin: "0", fontSize: "12px", color: "#666" }}>
                Workflow
              </p>
            </div>
            <select
              className="form-select"
              onChange={handleWorkflowChange}
              value={selectedWorkFlow}
              style={{
                height: 40,
                width: isSmallScreen ? 130 : 140,
              }}
            >
              <option
                value=""
                style={{
                  fontSize: isSmallScreen ? "11px" : "14px",
                }}
              >
                All Workflows
              </option>
              {workflow?.results?.map((workflowItem) => (
                <option
                  key={workflowItem.id}
                  value={workflowItem.id}
                  style={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    fontSize: isSmallScreen ? "11px" : "14px",
                  }}
                >
                  {workflowItem.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={`d-flex align-items-center gap-3 ${isExtraSmallScreen ? "w-100 justify-content-end" : ""}`} style={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            className="fw-bold"
            onClick={handleExportCSV}
            disabled={!rows.length}
            sx={actionButtonSx}
          >
            <i className="bi bi-download fs-2" style={{ color: ACTIVITY_PRIMARY }} />
            <span
              className="fw-bold"
              style={{
                textTransform: "none",
                whiteSpace: "nowrap",
                fontSize: isSmallScreen ? "11px" : "14px",
                color: ACTIVITY_PRIMARY,
              }}
            >
              Export CSV
            </span>
          </Button>
          <Button
            variant="outlined"
            className="fw-bold"
            onClick={openFilterModal}
            sx={actionButtonSx}
          >
            <i className="bi bi-filter fs-2" style={{ color: ACTIVITY_PRIMARY }} />
            <span
              className="fw-bold"
              style={{
                textTransform: "none",
                whiteSpace: "nowrap",
                fontSize: isSmallScreen ? "11px" : "14px",
                color: ACTIVITY_PRIMARY,
              }}
            >
              {isSmallScreen ? "Filter" : "Search filter"}
            </span>
          </Button>
        </div>
      </div>
      <ReactModal
        isOpen={isFilterModalOpen}
        onRequestClose={closeFilterModal}
        style={customFilterModalStyles}
        contentLabel="SL Breach Filter Modal"
        shouldCloseOnOverlayClick={false}
      >
        <SLBreachFilterModal
          closeFilterModal={closeFilterModal}
          selectedFilterAndValue={selectedFilters1}
          setSelectedFilterAndValue={setSelectedFilters1}
          handleSubmit={handleSubmit}
          taskSource={task_source}
          selectedSlType={task_source === "tickets" ? selectedSlType : "workflow"}
          selectedWorkFlow={task_source === "tickets" ? selectedWorkFlow : ""}
        />
      </ReactModal>
      <TableContainer
        component={Paper}
        ref={scrollContainerRef}
        style={{ maxHeight: 617, overflowY: "auto" }}
      >
        <Table stickyHeader aria-label="sl breach table">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...stickyCheckboxCellStyle,
                  width: "60px",
                  minWidth: "60px",
                }}
              >
                Sr.no.
              </TableCell>
              {task_source === "tickets" && (
                <TableCell
                  sx={{ ...stickyCellStyle, width: "80px", minWidth: "80px" }}
                >
                  ID
                </TableCell>
              )}
              <TableCell sx={{ ...stickyCellStyle, minWidth: "200px" }}>
                Subject / Title
              </TableCell>
              <TableCell sx={{ ...stickyCellStyle, minWidth: "130px" }}>
                Customer name
              </TableCell>
              {task_source === "tickets" && (
                <TableCell sx={{ ...stickyCellStyle, minWidth: "120px" }}>
                  Customer tag
                </TableCell>
              )}
              <TableCell sx={{ ...stickyCellStyle, minWidth: "110px" }}>
                Assignee
              </TableCell>
              {task_source === "tickets" && (
                <TableCell
                  sx={{ ...stickyCellStyle, width: "80px", minWidth: "80px" }}
                >
                  Priority
                </TableCell>
              )}
              <TableCell sx={{ ...stickyCellStyle, minWidth: "100px" }}>
                Disposition
              </TableCell>
              {task_source === "tickets" && (
                <TableCell sx={{ ...stickyCellStyle, minWidth: "130px" }}>
                  Workflow status
                </TableCell>
              )}
              <TableCell sx={{ ...stickyCellStyle, minWidth: "150px" }}>
                {getSlTargetColumnLabel(task_source, selectedSlType)}
              </TableCell>
              {task_source === "emails" && (
                <TableCell
                  sx={{ ...stickyCellStyle, width: "80px", minWidth: "80px" }}
                >
                  SL level
                </TableCell>
              )}
              <TableCell
                sx={{ ...stickyCellStyle, width: "130px", minWidth: "130px" }}
                align="center"
              >
                Time since breach
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={
                    task_source === "tickets"
                      ? ticketTableColSpan
                      : emailTableColSpan
                  }
                  align="center"
                >
                  <LoadingSpinner />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    task_source === "tickets"
                      ? ticketTableColSpan
                      : emailTableColSpan
                  }
                  align="center"
                  style={{ padding: "20px" }}
                >
                  No SL breached tasks available
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  component={Link}
                  to={
                    row.task_source === "email"
                      ? `/email/inbox/${row.id}`
                      : row.task_source === "ticket"
                      ? `/ticket/information/${row.id}`
                      : "#"
                  }
                  style={{ textDecoration: "none", color: "inherit" }}
                  onClick={() => storeScrollPosition(String(row.id))}
                >
                  <TableCell sx={{ width: "60px", minWidth: "60px", fontSize:isSmallScreen ? "12px" : "14px", }}>
                    {(currentPage - 1) * pageSize + index + 1}
                  </TableCell>
                  {task_source === "tickets" && (
                    <TableCell sx={{ width: "60px", minWidth: "60px", fontSize:isSmallScreen ? "12px" : "14px", }}>
                      {row.id}
                    </TableCell>
                  )}
                  <TableCell
                    sx={{ minWidth: "200px", fontSize:isSmallScreen ? "12px" : "14px", }}
                    style={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={row.subject}
                  >
                    {row.subject}
                  </TableCell>
                  <TableCell sx={{ minWidth: "150px", fontSize:isSmallScreen ? "12px" : "14px", }}>
                    {capitalizeFirstLetter(row.customer_name)}
                  </TableCell>
                  {task_source === "tickets" && (
                    <TableCell
                      sx={{ minWidth: "120px", fontSize: isSmallScreen ? "12px" : "14px" }}
                      style={{
                        maxWidth: 150,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={row.customer_tag}
                    >
                      {capitalizeFirstLetter(row.customer_tag)}
                    </TableCell>
                  )}
                  <TableCell sx={{ minWidth: "120px", fontSize:isSmallScreen ? "12px" : "14px", }}>
                    {capitalizeFirstLetter(row.current_assignee)}
                  </TableCell>
                  {task_source === "tickets" && (
                    <TableCell sx={{ width: "100px", minWidth: "100px", fontSize:isSmallScreen ? "12px" : "14px", }}>
                      <span
                        className={`badge ${
                          row.priority === "Urgent" || row.priority === "High"
                            ? "badge-danger"
                            : row.priority === "Medium"
                            ? "badge-warning"
                            : "badge-info"
                        }`}
                      >
                        {row.priority}
                      </span>
                    </TableCell>
                  )}
                  <TableCell sx={{ minWidth: "120px", fontSize:isSmallScreen ? "12px" : "14px", }}>
                    {capitalizeFirstLetter(row.disposition_name)}
                  </TableCell>
                  {task_source === "tickets" && (
                    <TableCell
                      sx={{ minWidth: "130px", fontSize: isSmallScreen ? "12px" : "14px" }}
                      style={{
                        maxWidth: 160,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={row.workflow_stage}
                    >
                      {capitalizeFirstLetter(row.workflow_stage)}
                    </TableCell>
                  )}
                  <TableCell sx={{ minWidth: "180px", fontSize:isSmallScreen ? "12px" : "14px", }}>
                    {row.sla_breach_time &&
                    !isNaN(new Date(row.sla_breach_time).getTime())
                      ? new Date(row.sla_breach_time).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })
                      : row.sla_breach_time || "NA"}
                  </TableCell>

                  {task_source === "emails" && (
                    <TableCell sx={{ width: "100px", minWidth: "100px", fontSize:isSmallScreen ? "12px" : "14px", }}>
                      {row.escalation_level || "NA"}
                    </TableCell>
                  )}
                  <TableCell
                    align="center"
                    sx={{
                      width: "150px",
                      minWidth: "150px",
                      textAlign: "center",
                      fontSize:isSmallScreen ? "12px" : "14px", 
                    }}
                  >
                    <span
                      className="badge badge-light-danger"
                      style={{
                        display: "inline-block",
                        minWidth: "80px",
                        fontVariantNumeric: "tabular-nums",
                      }}
                      key={`${row.id}-${currentTime}`}
                    >
                      {formatBreachTime(
                        row.time_since_breach,
                        componentMountedTime
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={`d-flex justify-content-between align-items-center mt-4 px-2 ${isPageScreen ? "flex-column gap-3" : ""}`}>
        <div className="d-flex align-items-center gap-4">
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: isSmallScreen ? "12px" : "14px", whiteSpace: "nowrap" }}>Show</span>
            <select
              className="form-select form-select-sm"
              style={{ width: "60px", fontSize: isSmallScreen ? "12px" : "14px", padding: isSmallScreen ? "2px 4px" : "2px 6px" }}
              name="perpage"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span style={{ fontSize: isSmallScreen ? "12px" : "14px", fontWeight: 500, color: "#495057" }}>
              per page
            </span>
          </div>
        </div>
        <div style={{ position: "sticky", bottom: 0, zIndex: 10 }}>
          <div className="d-flex align-items-center">
            <Pagination
              sx={{
                display: "flex",
                justifyContent: "end",
                "& .MuiPaginationItem-root": {
                  fontSize: isSmallScreen ? "11px" : "14px",
                  minWidth: isSmallScreen ? "28px" : "36px",
                  height: isSmallScreen ? "28px" : "36px",
                  margin: isSmallScreen ? "0 1px" : "0 2px",
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  color: "#374151",
                  "&:hover": {
                    backgroundColor: "#f9fafb",
                    borderColor: "#d1d5db",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#6366f1",
                    color: "#ffffff",
                    borderColor: "#6366f1",
                    "&:hover": {
                      backgroundColor: "#4f46e5",
                      borderColor: "#4f46e5",
                    },
                  },
                },
              }}
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              renderItem={(item) => {
                if (item.type === "page") {
                  const isNearCurrent =
                    Math.abs(item.page - currentPage) <= 1 ||
                    item.page === 1 ||
                    item.page === totalPages;
                  return isNearCurrent ? <PaginationItem {...item} /> : null;
                }
                return <PaginationItem {...item} />;
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SLBreachedTask;
